"use client";

import { Spinner } from "@/components/ui/spinner";

import { Inbox, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Empty,
  EmptyContent,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";

interface LoadingStatesProps {
  loading?: boolean;
  error?: string | null;
  forbidden?: boolean;
  forbiddenMessage?: string;
  onRetry?: () => void;
  emptyState?: boolean;
}

export function LoadingStates({
  loading,
  error,
  forbidden,
  forbiddenMessage,
  onRetry,
  emptyState,
}: LoadingStatesProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Spinner size={64} className="mx-auto mb-3 text-neutral-900" />
          <p className="text-gray-600">Cargando procesos...</p>
        </div>
      </div>
    );
  }

  if (forbidden) {
    return (
      <Alert variant="destructive" className="max-w-md mx-auto mt-8">
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>Acceso denegado</AlertTitle>
        <AlertDescription>
          {forbiddenMessage ?? 'No tienes acceso a este caso.'}
        </AlertDescription>
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert className="max-w-md mx-auto mt-8">
        <AlertDescription className="mb-3">{error}</AlertDescription>
        {onRetry && (
          <Button onClick={onRetry} variant="outline" size="sm">
            Reintentar
          </Button>
        )}
      </Alert>
    );
  }

  if (emptyState) {
    return (
      <Empty className="mx-auto max-w-md border-0 py-12 md:p-12">
        <EmptyHeader className="gap-2">
          <EmptyMedia variant="icon" className="text-gray-500">
            <Inbox className="h-5 w-5" aria-hidden="true" />
          </EmptyMedia>
          <EmptyTitle className="text-sm font-medium tracking-normal text-gray-500">
            No se encontraron procesos disponibles
          </EmptyTitle>
        </EmptyHeader>
        {onRetry && (
          <EmptyContent>
            <Button onClick={onRetry} variant="outline">
              Recargar
            </Button>
          </EmptyContent>
        )}
      </Empty>
    );
  }

  return null;
}
